<script setup lang="ts">
    import { onMounted, reactive, ref, watch } from 'vue';
    import axios from 'axios';
    import { useRoute, useRouter } from 'vue-router';
    import moment from 'moment';
    import OnboardingPersonalInfosForm from '@/views/collectivites/onboarding/OnboardingPersonalInfosForm.vue';

    const router = useRouter();
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const currentStep = ref(1);
    const startupFormDatas = reactive({
        pays: [] as any[],
        users: [] as any[]
    });
    const gerantType = ref<'new' | 'existing'>('new'); // Type de gérant : 'new' ou 'existing'
    const gerantFormRef = ref<InstanceType<typeof OnboardingPersonalInfosForm> | null>(null);
    const formData = reactive({
        id: null as number | null,
        nom: "",
        rccm: "",
        adresse: "",
        telephone: "",
        email: "",
        date_creation: "",
        pays_id: null as number | null,
        secteurs: [] as string[],
        gerant_id: null as number | null // ID de l'utilisateur existant à associer
    });
    const errors = reactive({
        nom: "",
        rccm: "",
        adresse: "",
        telephone: "",
        email: "",
        date_creation: "",
        pays_id: "",
        secteurs: "",
        gerant_id: ""
    });
    const secteurs: any[] = [
      { id: 'finance', value: 'Finance', label: 'Finance' },
      { id: 'informatique', value: 'Informatique', label: 'Informatique' },
      { id: 'agriculture', value: 'Agriculture', label: 'Agriculture' },
      { id: 'education', value: 'Éducation', label: 'Éducation' },
      { id: 'sante', value: 'Santé', label: 'Santé' },
      { id: 'transport', value: 'Transport', label: 'Transport' },
      { id: 'construction', value: 'Construction', label: 'Construction' },
      { id: 'commerce', value: 'Commerce', label: 'Commerce' },
      { id: 'industrie', value: 'Industrie', label: 'Industrie' },
      { id: 'autre', value: 'Autre', label: 'Autre' }
    ]

    onMounted(() => {
        const form_datas = route.meta.form_datas as { collectivite: any, startupFormDatas: any };
        Object.assign(startupFormDatas, form_datas.startupFormDatas);
        if (form_datas.collectivite) {
            const collectivite = form_datas.collectivite as any;
            if (collectivite.date_creation) {
                const date_creation = moment(collectivite.date_creation);
                collectivite.date_creation = date_creation.isValid() ? date_creation.format('YYYY-MM-DD') : '';
            }
            Object.assign(formData, collectivite);
        }
    });

    function validateStep1() {
        let isValid = true;
        errors.nom = '';
        if (!formData.nom) {
            errors.nom = "Le nom est requis";
            isValid = false;
        }

        errors.pays_id = '';
        if (!formData.pays_id || formData.pays_id <= 0) {
            errors.pays_id = "Le pays est requis";
            isValid = false;
        }

        errors.rccm = '';
        if (!formData.rccm) {
            errors.rccm = "Le RCCM est requis";
            isValid = false;
        }

        errors.adresse = '';
        if (!formData.adresse) {
            errors.adresse = "L'adresse est requise";
            isValid = false;
        }

        errors.telephone = '';
        if (!formData.telephone) {
            errors.telephone = "Le téléphone est requis";
            isValid = false;
        }

        errors.email = '';
        if (!formData.email) {
            errors.email = "L'email est requis";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Le format de l'email est invalide";
            isValid = false;
        }

        errors.secteurs = '';
        if (!formData.secteurs || formData.secteurs.length === 0) {
            errors.secteurs = "Au moins un secteur est requis";
            isValid = false;
        }

        errors.date_creation = '';
        if (!formData.date_creation) {
            errors.date_creation = "La date de création est requise";
            isValid = false;
        }

        return isValid;
    }

    function validateStep2() {
        let isValid = true;
        errors.gerant_id = '';

        if (gerantType.value === 'existing') {
            // Si on sélectionne un utilisateur existant, vérifier que gerant_id est défini
            if (!formData.gerant_id) {
                errors.gerant_id = "Veuillez sélectionner un utilisateur existant";
                isValid = false;
            }
        } else {
            // Si on crée un nouveau gérant, utiliser la validation du composant
            if (gerantFormRef.value) {
                isValid = gerantFormRef.value.validate();
            }
        }

        return isValid;
    }

    function validate() {
        if (currentStep.value === 1) {
            return validateStep1();
        } else if (currentStep.value === 2) {
            return validateStep2();
        }
        return true;
    }

    function goToNextStep() {
        if (currentStep.value === 1) {
            if (validateStep1()) {
                currentStep.value = 2;
                errorMessage.value = '';
            }
        }
    }

    function goToPreviousStep() {
        if (currentStep.value === 2) {
            currentStep.value = 1;
            errorMessage.value = '';
        }
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            // Utiliser FormData pour supporter l'upload de photo
            const formToUpload = new FormData();

            if (formData.id) {
                formToUpload.append('id', String(formData.id));
            }
            formToUpload.append('nom', formData.nom);
            formToUpload.append('rccm', formData.rccm);
            formToUpload.append('adresse', formData.adresse);
            formToUpload.append('telephone', formData.telephone);
            formToUpload.append('email', formData.email);
            formToUpload.append('pays_id', String(formData.pays_id));
            formToUpload.append('date_creation', formData.date_creation);
            formData.secteurs.forEach((secteur, index) => {
                formToUpload.append(`secteurs[${index}]`, secteur);
            });

            // En mode création (stepper), ajouter les données du gérant
            if (!formData.id && currentStep.value === 2) {
                if (gerantType.value === 'existing' && formData.gerant_id) {
                    // Associer un utilisateur existant
                    formToUpload.append('gerant_id', String(formData.gerant_id));
                } else if (gerantType.value === 'new' && gerantFormRef.value) {
                    // Créer un nouveau gérant avec les données du formulaire
                    const gerantData = gerantFormRef.value.formData;
                    formToUpload.append('gerant[nom]', gerantData.nom || '');
                    formToUpload.append('gerant[prenom]', gerantData.prenom || '');
                    formToUpload.append('gerant[telephone]', gerantData.telephone || '');
                    formToUpload.append('gerant[email]', gerantData.email || '');
                    if (gerantData.date_naissance) {
                        formToUpload.append('gerant[date_naissance]', gerantData.date_naissance);
                    }
                    formToUpload.append('gerant[age]', String(gerantData.age || 0));
                    if (gerantData.date_engagement) {
                        formToUpload.append('gerant[date_engagement]', gerantData.date_engagement);
                    }
                    formToUpload.append('gerant[anciennete]', String(gerantData.anciennete || 0));
                    formToUpload.append('gerant[salaire]', gerantData.salaire || '');
                    formToUpload.append('gerant[quotite_cessible_pourcent]', gerantData.quotite_cessible_pourcent || '');
                    formToUpload.append('gerant[quotite_cessible_montant]', gerantData.quotite_cessible_montant || '');
                }
            }

            await axios.post(`/backoffice/collectivites/save`, formToUpload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            router.push('/app/collectivites/list');
        } catch(error: any) {
            errorMessage.value = error.response?.data?.message || error.message || "Une erreur est survenue";
            console.error(error);
        } finally {
            loading.value = false;
        }
    }
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-30"></div>

        <section class="s20_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5">Collectivités / Edition <span class="text-sm text-grey-500"></span></h4>
                
                <!-- Stepper Navigation (uniquement en mode création) -->
                <div v-if="!formData.id" class="stepper-navigation mb-6">
                    <div class="stepper-steps">
                        <div class="stepper-step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
                            <div class="stepper-step-number">1</div>
                            <div class="stepper-step-label">Collectivité</div>
                        </div>
                        <div class="stepper-line" :class="{ completed: currentStep > 1 }"></div>
                        <div class="stepper-step" :class="{ active: currentStep === 2 }">
                            <div class="stepper-step-number">2</div>
                            <div class="stepper-step-label">Gérant</div>
                        </div>
                    </div>
                </div>

                <!-- Étape 1 : Collectivité -->
                <div v-show="!formData.id ? currentStep === 1 : true">
                <div class="s10_mobile container-full-2"> 
                    <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                        <img src="/public/icons/icon-info-collectivite.svg" class="size-l" alt="" /> 
                        Informations
                    </h2>
                    
                    <div class="form-field-floating lg reactive active my-5">
                        <input type="text" class="form-input" name="nom" id="nom" v-model="formData.nom" placeholder="" />
                        <label for="nom">Désignation de la collectivité <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>

                    <div class="form-field-floating lg reactive active my-5">
                        <input type="text" class="form-input" name="rccm" id="rccm" v-model="formData.rccm" placeholder="" />
                        <label for="rccm">Numéro du registre de commerce <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['rccm']" class="text-red">{{ errors['rccm'] }}</span>

                    <div class="form-field-floating active my-4">
                        <select class="form-input-select" name="pays_id" id="pays_id" v-model="formData.pays_id">
                            <option :value="null" selected>Sélectionnez un pays</option>
                            <option :value="pays.id" v-for="(pays, index) in startupFormDatas.pays" :key="'pays_' + index">
                                {{ pays.nom }}
                            </option>
                        </select>
                        <label for="pays_id">Pays <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['pays_id']" class="text-red">{{ errors['pays_id'] }}</span>

                    <div class="form-field-floating lg reactive my-5">
                        <input type="text" class="form-input" name="adresse" id="adresse" v-model="formData.adresse" placeholder="" />
                        <label for="adresse">Adresse de l'entreprise <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="!!errors['adresse']" class="text-red">{{ errors['adresse'] }}</span>

                    <div class="form-field-split active my-5">
                        <custom-phone-input v-model="formData.telephone" id="telephone" classesNames="half" />
                        <hr class="hr" />
                        <div class="form-field-floating lg reactive half">
                            <input type="email" class="form-input" name="email" id="email" v-model="formData.email" placeholder="" />
                            <label for="email">Adresse Email <b class="color-danger">*</b></label>
                        </div>
                    </div>
                    <span v-if="!!errors['telephone']" class="text-red">{{ errors['telephone'] }}</span>
                    <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>
                    
                    <div class="form-field-floating lg reactive my-5">
                        <input type="date" class="form-input" name="date_creation" id="date_creation" v-model="formData.date_creation" placeholder="" />
                        <label for="date_creation">Date de création</label>
                    </div>
                    <span v-if="!!errors['date_creation']" class="text-red">{{ errors['date_creation'] }}</span>
                </div>  
                
                <div class="s10_mobile container-full-2">
                    <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                        <img src="/public/icons/icon-secteur-activite.svg" class="size-l" alt="" /> 
                        Secteur d'activité
                    </h2>
                    
                    <div class="grid2 grid-row-gap-0">
                        <div v-for="secteur in secteurs" :key="secteur.id" class="card-check lg single">
                            <label class="form-check-label" :for="`secteur_${secteur.id}`">
                                <div class="card-check-text">
                                    <input class="form-check-input left" type="checkbox" name="secteur[]" :id="`secteur_${secteur.id}`" :value="secteur.value" v-model="formData.secteurs" />
                                    <h6 class="font-semibold mt-1 mb-2 align-right">{{ secteur.label }}</h6>
                                </div>  
                            </label>
                        </div>
                    </div>
                    <span v-if="!!errors['secteurs']" class="text-red">{{ errors['secteurs'] }}</span>
                    </div>
                </div>

                <!-- Étape 2 : Gérant (uniquement en mode création) -->
                <div v-if="!formData.id" v-show="currentStep === 2">
                    <div class="s10_mobile container-full-2">
                        <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                            <img src="/public/icons/icon-info-perso.svg" class="size-l" alt="" />
                            Membre Gérant
                        </h2>

                        <!-- Choix du type de gérant -->
                        <div class="my-5">
                            <label class="font-medium mb-3 block">Type de gérant <b class="color-danger">*</b></label>
                            <div class="grid2 grid-row-gap-0">
                                <div class="card-check lg single">
                                    <label class="form-check-label" for="gerant_type_new">
                                        <div class="card-check-text">
                                            <input
                                                class="form-check-input left"
                                                type="radio"
                                                name="gerant_type"
                                                id="gerant_type_new"
                                                value="new"
                                                v-model="gerantType"
                                            />
                                            <h6 class="font-semibold mt-1 mb-2 align-right">Créer un nouveau gérant</h6>
                                        </div>
                                    </label>
                                </div>
                                <div class="card-check lg single">
                                    <label class="form-check-label" for="gerant_type_existing">
                                        <div class="card-check-text">
                                            <input
                                                class="form-check-input left"
                                                type="radio"
                                                name="gerant_type"
                                                id="gerant_type_existing"
                                                value="existing"
                                                v-model="gerantType"
                                            />
                                            <h6 class="font-semibold mt-1 mb-2 align-right">Utiliser un utilisateur existant</h6>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Sélection d'un utilisateur existant -->
                        <div v-if="gerantType === 'existing'">
                            <div class="form-field-floating active my-5">
                                <select class="form-input-select" name="gerant_id" id="gerant_id" v-model="formData.gerant_id">
                                    <option :value="null" selected>Sélectionnez un utilisateur</option>
                                    <option :value="user.id" v-for="(user, index) in startupFormDatas.users" :key="'user_' + index">
                                        {{ user.fullname }} - {{ user.email }} {{ user.fonction ? '(' + user.fonction + ')' : '' }}
                                    </option>
                                </select>
                                <label for="gerant_id">Utilisateur existant <b class="color-danger">*</b></label>
                            </div>
                            <span v-if="!!errors.gerant_id" class="text-red">{{ errors.gerant_id }}</span>
                            <p v-if="startupFormDatas.users.length === 0" class="text-grey-500 text-sm mt-2">
                                Aucun utilisateur disponible. Créez d'abord un utilisateur ou choisissez "Créer un nouveau gérant".
                            </p>
                        </div>
                    </div>

                    <!-- Formulaire complet pour créer un nouveau gérant -->
                    <onboarding-personal-infos-form
                        v-if="gerantType === 'new'"
                        ref="gerantFormRef"
                        :collectivites="[]"
                        :hide-collectivite-field="true"
                        :hide-submit-button="true"
                    />
                </div>

                <div class="clear-both"></div>

                <!-- Actions -->
                <div class="s10_mobile container-full-2">
                    <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                    <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                    <div class="flex gap-2">
                        <!-- Bouton Précédent (étape 2 uniquement) -->
                        <custom-button v-if="!formData.id && currentStep === 2" id="btn-prev" type="button" :classesList="'btn lg'" @click="goToPreviousStep()">
                            <span>Précédent</span>
                        </custom-button>
                        
                        <!-- Bouton Suivant (étape 1 uniquement en mode création) -->
                        <custom-button v-if="!formData.id && currentStep === 1" id="btn-next" type="button" :classesList="'btn lg w-100'" @click="goToNextStep()">
                            <span>Suivant</span>
                        </custom-button>
                        
                        <!-- Bouton Enregistrer (étape 2 ou mode édition) -->
                        <custom-button v-if="formData.id || (!formData.id && currentStep === 2)" id="btn-submit" type="button" :classesList="'btn lg w-100'" :loading="loading" @click="onSubmit()">
                            <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                            <span>Enregistrer</span>
                        </custom-button>
                    </div>
                </div>

                <div class="clear-both h-25"></div>
            </div>
        </section>
        
    </section>
</template>

<style scoped>
.stepper-navigation {
    width: 100%;
    margin-bottom: 2rem;
}

.stepper-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    max-width: 500px;
    margin: 0 auto;
}

.stepper-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    position: relative;
}

.stepper-step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
    background-color: rgba(100, 100, 100, 0.1);
    color: rgba(100, 100, 100, 0.6);
    border: 2px solid rgba(100, 100, 100, 0.2);
    transition: all 0.3s ease;
}

.stepper-step.active .stepper-step-number {
    background-color: rgb(var(--component-color-rgb), 1);
    color: var(--color-white);
    border-color: rgb(var(--component-color-rgb), 1);
    box-shadow: 0 0 0 0.4rem rgba(var(--component-color-rgb), 0.1);
}

.stepper-step.completed .stepper-step-number {
    background-color: rgb(var(--color-success-rgb), 1);
    color: var(--color-white);
    border-color: rgb(var(--color-success-rgb), 1);
}

.stepper-step-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(100, 100, 100, 0.6);
    transition: all 0.3s ease;
}

.stepper-step.active .stepper-step-label {
    color: rgb(var(--component-color-rgb), 1);
    font-weight: 600;
}

.stepper-step.completed .stepper-step-label {
    color: rgb(var(--color-success-rgb), 1);
}

.stepper-line {
    flex: 1;
    height: 2px;
    background-color: rgba(100, 100, 100, 0.2);
    transition: all 0.3s ease;
}

.stepper-line.completed {
    background-color: rgb(var(--color-success-rgb), 1);
}

@media (max-width: 768px) {
    .stepper-steps {
        gap: 0.5rem;
    }
    
    .stepper-step-number {
        width: 35px;
        height: 35px;
        font-size: 1rem;
    }
    
    .stepper-step-label {
        font-size: 0.8rem;
    }
}
</style>
